import { Grid } from "@mui/material";
import { useTheme } from "@mui/system";
import {
    DateRangeFilter,
    HierarchiesFilter,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents";
import ManageFiltersButton from "components/ApplicationFilters/ManageFiltersSignals/ManageFiltersButton";
import AcxButton from "components/UI/AcxButton";
import React, { useState } from "react";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { useStore } from "utils/useStore";
import SignalsPageStore from "../../Store/SignalsPageStore";
import { SignalsDashboardType } from "../../SignalsModels";
import { PinFilterButton } from "./PinFilterButton";
import AppliedFiltersChips from "components/ApplicationFilters/FiltersAndChips/AppliedFiltersChips";
import { observer } from "mobx-react";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { toJS } from "mobx";
import _ from "lodash";
import ObjectFilterConflictDialog from "./ObjectFilterConflictDialog";

export const DashboardFiltersSection = observer(() => {
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const signalsPageStore = useStore(SignalsPageStore);
    const messageStore = useStore(MessageStore);
    const currentDashboard = signalsPageStore.currentDashboard;
    const theme = useTheme();
    const [isResolvingConflicts, setIsResolvingConflicts] = useState(false);

    if (!currentDashboard || currentDashboard.isPresentationMode) return null;

    const dashboardHasPinnedFilter =
        currentDashboard?.dashboardType === SignalsDashboardType.Custom &&
        !!currentDashboard?.pinnedApplicationFilterId;

    // When a dashboard has a pinned filter, filters should be view-only until
    // the pin is removed (matches SignalsV1 behaviour).
    const filtersLockedByPinned = dashboardHasPinnedFilter;

    const currentlyPinnedFilterValue =
        applicationFiltersStore.savedFilters.find(
            (sf) => sf.id === currentDashboard?.pinnedApplicationFilterId,
        )?.value;

    const pinnedFilterHasDateRange =
        !!currentlyPinnedFilterValue?.startDate ||
        !!currentlyPinnedFilterValue?.dateRangeLabel;

    return (
        <Grid
            container
            direction="column"
            spacing={0.5}
            sx={{
                padding: theme.spacing(1),
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
            }}
        >
            <Grid
                container
                item
                alignItems="center"
                direction="row"
                flexWrap="nowrap"
                flexGrow={2}
            >
                <Grid
                    container
                    item
                    alignItems="stretch"
                    justifyContent="space-between"
                    flexWrap="nowrap"
                    sx={{ display: "flex", flexDirection: "row" }}
                >
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            height: "100%",
                            alignItems: "center",
                            justifyItems: "start",
                            gap: 1,
                        }}
                    >
                        <Grid sx={{ display: "flex" }}>
                            <ManageFiltersButton
                                title="Filters"
                                subtitle="Define the criteria that will determine which conversations are included or excluded in Signals reporting."
                                savedFilterDomains={
                                    ApplicationFiltersStore.AllSavedFilterDomains
                                }
                                onApply={() => {
                                    currentDashboard.applyFilters("manual");
                                }}
                                disabled={filtersLockedByPinned}
                            />
                        </Grid>

                        <Grid sx={{ display: "flex" }}>
                            <DateRangeFilter
                                store={
                                    applicationFiltersStore.quickFiltersStore
                                }
                                filterKey="dateRange"
                                maxWidth="350px"
                                inputStyle={{ width: "350px" }}
                                wrapperStyle={{ maxWidth: "350px" }}
                                onSelect={() => {
                                    if (dashboardHasPinnedFilter) return;
                                    applicationFiltersStore.alertOfQuickFilterOverride(
                                        "dateRange",
                                    );
                                }}
                                viewOnly={
                                    filtersLockedByPinned &&
                                    pinnedFilterHasDateRange
                                }
                            />
                        </Grid>
                        <Grid sx={{ display: "flex" }}>
                            <HierarchiesFilter
                                width="184px"
                                popperWidth="500px"
                                store={
                                    applicationFiltersStore.quickFiltersStore
                                }
                                filterKey="hierarchyIds"
                                onSaveUpdateWithHierarchies={() => {
                                    if (dashboardHasPinnedFilter) return;
                                    applicationFiltersStore.alertOfQuickFilterOverride(
                                        "hierarchyIds",
                                    );
                                }}
                                viewOnly={filtersLockedByPinned}
                                placeholder={
                                    filtersLockedByPinned
                                        ? "No hierarchies applied"
                                        : undefined
                                }
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-end",
                            flexDirection: "row",
                        }}
                    >
                        <Grid
                            container
                            item
                            direction="row"
                            alignItems="center"
                            width="fit-content"
                            flexWrap="nowrap"
                            gap={1}
                        >
                            {!filtersLockedByPinned &&
                                (applicationFiltersStore.appliedFilterCount >
                                    0 ||
                                    applicationFiltersStore.quickFiltersStore
                                        .appliedFilterCount > 0) && (
                                    <Grid item>
                                        <AcxButton
                                            id="signals-filters-clear"
                                            variant="text"
                                            onClick={() => {
                                                applicationFiltersStore.resetFilters();
                                                applicationFiltersStore.setSelectedSavedFilters(
                                                    undefined,
                                                );
                                                applicationFiltersStore.quickFiltersStore.resetFilters();

                                                currentDashboard.applyFilters("manual");
                                                signalsPageStore.setShouldResetBuilder(
                                                    true,
                                                );
                                            }}
                                            disabled={
                                                signalsPageStore
                                                    .currentDashboard
                                                    ?.pinnedApplicationFilterId
                                                    ? true
                                                    : false
                                            }
                                            disabledTooltip={
                                                signalsPageStore
                                                    .currentDashboard
                                                    ?.pinnedApplicationFilterId
                                                    ? "You must unpin your filter before clearing"
                                                    : "Disabled"
                                            }
                                            sx={{
                                                color: theme.palette.blue.main,
                                                ":hover": {
                                                    backgroundColor:
                                                        theme.palette.white
                                                            .main,
                                                },
                                            }}
                                        >
                                            Clear
                                        </AcxButton>
                                    </Grid>
                                )}
                            <Grid item>
                                <AcxButton
                                    id="signals-filters-apply"
                                    color="primary"
                                    onClick={() => {
                                        currentDashboard.applyFilters("manual");
                                    }}
                                    loading={currentDashboard.isRefreshing}
                                    disabled={
                                        applicationFiltersStore.hasError ||
                                        applicationFiltersStore
                                            .wordsAndPhrasesStore.hasError
                                    }
                                >
                                    Apply
                                </AcxButton>
                            </Grid>
                            {currentDashboard.isRefreshing && (
                                <Grid item>
                                    <AcxButton
                                        id="signals-filters-cancel"
                                        variant="outlined"
                                        onClick={() => {
                                            currentDashboard.cancelRefresh();
                                            if (
                                                !_.isEqual(
                                                    toJS(
                                                        currentDashboard.lastAppliedFilters,
                                                    ),
                                                    applicationFiltersStore.toFilterObject(),
                                                )
                                            ) {
                                                messageStore.logInfo(
                                                    "Because you hit cancel, displayed data does not match your filters.",
                                                );
                                            }
                                        }}
                                        sx={(theme) => ({
                                            color: theme.palette.red.main,
                                            borderColor:
                                                theme.palette.lightgrayBorder
                                                    .main,
                                            backgroundColor:
                                                theme.palette.white.main,
                                            ":hover": {
                                                borderColor:
                                                    theme.palette
                                                        .lightgrayBorder.main,
                                                backgroundColor:
                                                    theme.palette.white.main,
                                                opacity: "75%",
                                            },
                                        })}
                                    >
                                        Cancel
                                    </AcxButton>
                                </Grid>
                            )}
                            {signalsPageStore.currentDashboard
                                ?.dashboardType ===
                                SignalsDashboardType.Custom && (
                                <PinFilterButton />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item flexGrow={1}>
                <AppliedFiltersChips
                    disabled={filtersLockedByPinned}
                    hideDateRangeChip
                    appliedFilters={currentDashboard.effectiveAppliedFilters}
                />
            </Grid>
            <ObjectFilterConflictDialog
                isOpen={currentDashboard.isObjectFilterConflictDialogOpen}
                conflicts={currentDashboard.objectFilterConflicts}
                onCancel={() => {
                    currentDashboard.cancelApplyWithObjectFilterConflicts();
                }}
                onContinue={async () => {
                    setIsResolvingConflicts(true);
                    try {
                        await currentDashboard.continueApplyWithObjectFilterConflicts();
                        messageStore.logInfo("Conflicting chart filters were removed to match your dashboard settings.")
                    } finally {
                        setIsResolvingConflicts(false);
                    }
                }}
                isContinueLoading={isResolvingConflicts}
            />
        </Grid>
    );
});
